import { useEffect } from "react";
import { Divider } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ComponentProps } from "react";
import {
  AutocompleteArrayInput,
  BooleanInput,
  DateInput,
  ReferenceArrayInput,
  SelectInput,
  TextInput,
  maxValue,
  minValue,
  required,
  useGetMany,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

import { RAZA_EVENT_START_TIME_CHOICES } from "@/utils/raza-event-time";

export {
  transformMiqaatPayload,
  validateMiqaatForm,
  validateMiqaatRsvpCutoff,
  validateMiqaatSelfCheckInOpensHours,
  validateMiqaatVenues,
} from "./transform-miqaat-payload";

const mealChoices = [
  { id: "LUNCH", name: "Lunch (afternoon slot)" },
  { id: "DINNER", name: "Dinner (evening slot)" },
];

const DEFAULT_LUNCH_START = "13:30";
const DEFAULT_DINNER_START = "19:30";

const dateModeChoices = [
  { id: "PERMANENT", name: "Permanent (annual Hijri)" },
  { id: "TEMPORARY", name: "Temporary (one Gregorian date)" },
];

/** Numeric keyboard without empty → 0 (avoids bogus month 0 when the field is left blank). */
function HijriDigitInput(props: ComponentProps<typeof TextInput>) {
  const { parse: _parse, format: _format, ...rest } = props;
  return (
    <TextInput
      {...rest}
      type="text"
      slotProps={{
        htmlInput: {
          inputMode: "numeric",
          pattern: "[0-9]*",
          onInput: (e: { currentTarget: HTMLInputElement }) => {
            const t = e.currentTarget as HTMLInputElement;
            t.value = t.value.replace(/[^0-9]/g, "");
          },
        },
      }}
      parse={(v) => (v === "" || v === undefined || v === null ? undefined : Number(String(v)))}
      format={(v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "" : String(v))}
    />
  );
}

function DefaultVenueSelect() {
  const venueIdsRaw = useWatch({ name: "venueIds" });
  const venueIds = Array.isArray(venueIdsRaw)
    ? venueIdsRaw.filter((id): id is string => typeof id === "string" && id.length > 0)
    : [];
  const { data: venues } = useGetMany(
    "miqaatVenues",
    { ids: venueIds },
    { enabled: venueIds.length >= 2 }
  );

  if (venueIds.length < 2) {
    return null;
  }

  const byId = new Map((venues ?? []).map((v) => [String(v.id), String(v.name ?? v.id)]));
  const choices = venueIds.map((id) => ({
    id,
    name: byId.get(id) ?? id,
  }));

  return (
    <SelectInput
      source="defaultVenueId"
      label="Default RSVP venue"
      choices={choices}
      fullWidth
      helperText="Used when the household does not override per member."
    />
  );
}

function FamilySelfCheckInInput() {
  const selfCheckInEnabled = Boolean(useWatch({ name: "selfCheckInEnabled" }));
  const familySelfCheckInEnabled = useWatch({ name: "familySelfCheckInEnabled" });
  const { setValue } = useFormContext();

  useEffect(() => {
    // Always persist an explicit boolean; default off when unset or when self-scan is off.
    if (!selfCheckInEnabled) {
      if (familySelfCheckInEnabled !== false) {
        setValue("familySelfCheckInEnabled", false, { shouldDirty: false });
      }
      return;
    }
    if (familySelfCheckInEnabled == null) {
      setValue("familySelfCheckInEnabled", false, { shouldDirty: false });
    }
  }, [selfCheckInEnabled, familySelfCheckInEnabled, setValue]);

  return (
    <BooleanInput
      source="familySelfCheckInEnabled"
      label="Allow one person to check in other family members"
      defaultValue={false}
      disabled={!selfCheckInEnabled}
      fullWidth
      helperText={
        selfCheckInEnabled
          ? "Directory-matched senders with a household of 2+ get a family check-in form. Visitors stay single-person."
          : "Turn on WhatsApp self-scan first."
      }
    />
  );
}

/** When meal type changes, suggest the standard start time if the field still matches the other meal's default. */
function MealTypeAndStartTimeFields() {
  const mealType = useWatch({ name: "mealType" });
  const startTime = useWatch({ name: "startTime" });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (mealType !== "LUNCH" && mealType !== "DINNER") return;
    const mealDefault = mealType === "LUNCH" ? DEFAULT_LUNCH_START : DEFAULT_DINNER_START;
    const otherDefault = mealType === "LUNCH" ? DEFAULT_DINNER_START : DEFAULT_LUNCH_START;
    if (startTime == null || startTime === "" || startTime === otherDefault) {
      setValue("startTime", mealDefault, { shouldDirty: false });
    }
  }, [mealType, startTime, setValue]);

  return (
    <>
      <SelectInput source="mealType" label="Meal slot" choices={mealChoices} isRequired fullWidth />
      <SelectInput
        source="startTime"
        label="Start time"
        choices={RAZA_EVENT_START_TIME_CHOICES}
        isRequired
        fullWidth
        helperText="IST wall clock. Defaults to 1:30 PM for lunch and 7:30 PM for dinner."
        validate={required()}
      />
    </>
  );
}

export default function MiqaatFormFields() {
  const dateMode = useWatch({ name: "dateMode" });

  return (
    <>
      <TextInput source="name" label="Miqaat name" fullWidth validate={required()} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Venues
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Link at least one venue from the Venues master. The RSVP template body uses the joined venue
        names. When two or more are linked, pick a default for RSVP.
      </Typography>
      <ReferenceArrayInput source="venueIds" reference="miqaatVenues" perPage={100}>
        <AutocompleteArrayInput
          optionText="name"
          label="Venues"
          fullWidth
          validate={required("Select at least one venue")}
          filterToQuery={(q: string) => ({ name: q })}
        />
      </ReferenceArrayInput>
      <DefaultVenueSelect />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        WhatsApp self-scan
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Turn on when this miqaat should allow WhatsApp live-location check-in. Requires linked
        venues with map coordinates. Family check-in is optional and only applies when self-scan is
        on.
      </Typography>
      <BooleanInput
        source="selfCheckInEnabled"
        label="Enable WhatsApp self-scan"
        defaultValue={false}
        fullWidth
      />
      <FamilySelfCheckInInput />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <TextInput
            source="selfCheckInOpensHoursBeforeEvent"
            label="Check-in opens (hours before slot)"
            type="number"
            fullWidth
            helperText="Default 0 = opens exactly at the start time above. Stays open for the rest of the day once open."
            validate={[required(), minValue(0), maxValue(8760)]}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Date & hall blocking
      </Typography>

      <SelectInput
        source="dateMode"
        label="Date type"
        fullWidth
        choices={dateModeChoices}
        isRequired
      />

      {dateMode === "PERMANENT" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Repeats every Hijri year on this month and day. Halls are blocked like a{" "}
          <strong>permanent</strong> blocked-hall entry (no Gregorian date).
        </Alert>
      )}
      {dateMode === "TEMPORARY" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Single calendar day only. Halls are blocked like a <strong>temporary</strong> blocked-hall
          entry using the Gregorian date below.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {dateMode === "PERMANENT" ? (
          <>
            <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
              <HijriDigitInput
                source="hijriMonth"
                label="Hijri month (0–11)"
                fullWidth
                helperText="0 = Moharram, 1 = Safar, …, 11 = Zilhaj"
                validate={[required(), minValue(0), maxValue(11)]}
              />
            </Box>
            <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
              <HijriDigitInput
                source="hijriDay"
                label="Hijri day (1–30)"
                fullWidth
                validate={[required(), minValue(1), maxValue(30)]}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
            <DateInput
              source="englishDate"
              label="Gregorian date"
              fullWidth
              validate={required()}
              isRequired
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <MealTypeAndStartTimeFields />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        WhatsApp RSVP cutoff
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        RSVP opens when the first successful <strong>Miqaat RSVP WhatsApp broadcast</strong> is sent
        from <strong>Admin → WhatsApp Broadcasts</strong> (Miqaat RSVP tab). Cutoff is measured in
        full hours before the start time (IST) on the miqaat date.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <TextInput
            source="rsvpClosesHoursBeforeEvent"
            label="RSVP closes / cutoff (hours before event)"
            type="number"
            fullWidth
            helperText="Default 6"
            validate={[required(), minValue(0), maxValue(8760)]}
          />
        </Box>
      </Box>
    </>
  );
}
